import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Search, Filter } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { projectTemplateService } from '../../services/projectTemplateService';
import AdminHeader from '../admin/components/AdminHeader';
import TemplateCard from './components/TemplateCard';
import TemplateFormModal from './components/TemplateFormModal';
import Button from '../../components/ui/Button';

const ProjectTemplatesManagement = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (profile?.role !== 'admin') {
        navigate('/projeto');
      } else {
        loadTemplates();
      }
    }
  }, [user, profile, authLoading, navigate]);

  useEffect(() => {
    filterTemplates();
  }, [searchQuery, selectedType, templates]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await projectTemplateService?.getAllTemplates();
      setTemplates(data || []);
      setFilteredTemplates(data || []);
    } catch (err) {
      setError('Erro ao carregar templates: ' + err?.message);
      console.error('Erro ao carregar templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered?.filter(template =>
        template?.templateName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        template?.projectType?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Filter by project type
    if (selectedType !== 'all') {
      filtered = filtered?.filter(template => template?.projectType === selectedType);
    }

    setFilteredTemplates(filtered);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await projectTemplateService?.duplicateTemplate(template?.id);
      setSuccess('Template duplicado com sucesso!');
      loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao duplicar template: ' + err?.message);
      console.error('Erro ao duplicar:', err);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Tem certeza que deseja excluir este template?')) {
      return;
    }

    try {
      await projectTemplateService?.deleteTemplate(templateId);
      setSuccess('Template excluído com sucesso!');
      loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir template: ' + err?.message);
      console.error('Erro ao excluir:', err);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleModalSuccess = () => {
    setSuccess(selectedTemplate ? 'Template atualizado com sucesso!' : 'Template criado com sucesso!');
    loadTemplates();
    handleModalClose();
    setTimeout(() => setSuccess(''), 3000);
  };

  const projectTypes = ['all', ...new Set(templates?.map(t => t?.projectType))];

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader userName={profile?.fullName || profile?.email} onSignOut={handleSignOut} />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
                <FileText className="h-6 w-6 sm:h-8 sm:w-8" />
                Gestão de Templates
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-2">
                Crie e gerencie templates pré-configurados para tipos comuns de projetos
              </p>
            </div>
            <Button onClick={handleCreateTemplate} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Criar Novo Template</span>
              <span className="sm:hidden">Novo</span>
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-muted-foreground">Total de Templates</p>
              <p className="text-xl sm:text-2xl font-bold text-foreground">{templates?.length}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-muted-foreground">Tipos de Projeto</p>
              <p className="text-xl sm:text-2xl font-bold text-foreground">{projectTypes?.length - 1}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-muted-foreground">Mais Usado</p>
              <p className="text-xl sm:text-2xl font-bold text-foreground">
                {templates?.[0]?.usageCount || 0}
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-muted-foreground">Uso Total</p>
              <p className="text-xl sm:text-2xl font-bold text-foreground">
                {templates?.reduce((sum, t) => sum + (t?.usageCount || 0), 0)}
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Buscar templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e?.target?.value)}
                className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Todos os Tipos</option>
                {projectTypes?.filter(t => t !== 'all')?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-success/10 border border-success/20 rounded-lg text-success">
            {success}
          </div>
        )}

        {/* Templates Grid */}
        {filteredTemplates?.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchQuery || selectedType !== 'all' ?'Nenhum template encontrado com os filtros aplicados' :'Nenhum template criado ainda'}
            </p>
            {!searchQuery && selectedType === 'all' && (
              <Button onClick={handleCreateTemplate} className="mt-4">
                Criar Primeiro Template
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredTemplates?.map(template => (
              <TemplateCard
                key={template?.id}
                template={template}
                onEdit={handleEditTemplate}
                onDuplicate={handleDuplicateTemplate}
                onDelete={handleDeleteTemplate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Template Form Modal */}
      <TemplateFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        template={selectedTemplate}
      />
    </div>
  );
};

export default ProjectTemplatesManagement;